import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Vehicle } from 'types/vehicle';
import { VehicleImageCard } from 'components/vehicle-history';
import { useCounterAmount, useLastOfferAmount } from '../hooks/useVehicleOffers';
import VinIcon from '../icons/vin.svg';
import MileageIcon from '../icons/mileage.svg';
import BuyerIcon from '../icons/seller.svg';
import ReservePriceIcon from '../icons/reserve_price.svg';
import DateSoldIcon from '../icons/date_sold.svg';
import AwardPriceIcon from '../icons/award_price.svg';

const Panel = styled.div<{ $width?: string; $background?: string }>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width ?? '360px'};
  padding: 11px;
  background: ${({ $background }) => $background ?? '#f9f9f9'};
  border-radius: 10px;
  flex-shrink: 0;
`;

const VehicleIdBold = styled.span`
  font-weight: 700;
`;

const VehicleIdSlug = styled.span`
  font-weight: 400;
`;

const AwardedLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`;

const VehicleTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-top: 10px;
  line-height: 1.3;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 6px;
  width: 100%;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const InfoContent = styled.div`
  display: flex;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  min-width: fit-content;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #000;
  min-width: fit-content;
`;

const formatPrice = (amount: number | string | null | undefined): string => {
  if (!amount) return 'N/A';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

interface VehicleLeftPanelProps {
  vehicle: Vehicle & { award_price?: number; buyer_name?: string };
  onImageClick?: () => void;
  showAwarded?: boolean;
  panelWidth?: string;
  panelBackground?: string;
  footer?: React.ReactNode;
}

const VehicleLeftPanel = ({ vehicle, onImageClick, showAwarded = true, panelWidth, panelBackground, footer }: VehicleLeftPanelProps) => {
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const imageUrl = vehicle.preview_image?.url;
  const auctionId = vehicle.auction?.slug ? `${vehicle.auction.slug}-${vehicle.orderIndex || '-'}` : null;
  const isAwarded = vehicle.status === 'awarded';

  const counterAmount = useCounterAmount(vehicle);
  const lastOfferAmount = useLastOfferAmount(vehicle);

  const reservePrice = formatPrice(vehicle.reserve_price);

  const dateSold = vehicle.transferRecord?.date_sold
    ? moment.utc(vehicle.transferRecord.date_sold).format('MM/DD/YYYY')
    : 'N/A';

  const buyerName = vehicle.awarded_user?.dealership_name ?? 'N/A';

  return (
    <Panel $width={panelWidth} $background={panelBackground}>
      <VehicleImageCard
        imageUrl={imageUrl}
        label={
          auctionId && (
            <>
              <VehicleIdBold>Vehicle ID: </VehicleIdBold>
              <VehicleIdSlug>{auctionId}</VehicleIdSlug>
            </>
          )
        }
        onClick={onImageClick}
        badge={showAwarded ? <AwardedLabel>Awarded</AwardedLabel> : undefined}
      />

      <VehicleTitle>{vehicleTitle}</VehicleTitle>

      <InfoList>
        <InfoRow>
          <VinIcon />
          <InfoContent>
            <InfoLabel>VIN:&nbsp;</InfoLabel>
            <InfoValue>{vehicle.vin}</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <ReservePriceIcon />
          <InfoContent>
            <InfoValue>Reserve:&nbsp;</InfoValue>
            <InfoLabel>{reservePrice}</InfoLabel>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          {isAwarded ? <DateSoldIcon /> : <ReservePriceIcon />}
          <InfoContent>
            {isAwarded ? (
              <>
                <InfoValue>Date Sold:&nbsp;</InfoValue>
                <InfoLabel>{dateSold}</InfoLabel>
              </>
            ) : (
              <>
                <InfoValue>Counter:&nbsp;</InfoValue>
                <InfoLabel>{formatPrice(counterAmount)}</InfoLabel>
              </>
            )}
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <AwardPriceIcon />
          <InfoContent>
            {isAwarded ? (
              <>
                <InfoValue>Award Price:&nbsp;</InfoValue>
                <InfoLabel>{formatPrice(vehicle.vehicle_price)}</InfoLabel>
              </>
            ) : (
              <>
                <InfoValue>Last Offer:&nbsp;</InfoValue>
                <InfoLabel>{formatPrice(lastOfferAmount)}</InfoLabel>
              </>
            )}
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <MileageIcon />
          <InfoContent>
            <InfoLabel>{vehicle.mileage?.toLocaleString()}&nbsp;</InfoLabel>
            <InfoValue>/Mileage</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <BuyerIcon />
          <InfoContent>
            <InfoLabel>{buyerName}</InfoLabel>
          </InfoContent>
        </InfoRow>
      </InfoList>
      {footer}
    </Panel>
  );
};

export default VehicleLeftPanel;
